/**
 * Ontology Structure Viewer
 *
 * Displays the complete FAIR Ontology v3 structure parsed from TTL file.
 * Shows class hierarchy, properties, individuals, and alignments.
 *
 * @module client/components/OntologyStructureViewer
 * @version 1.0
 * @date 2025-10-18
 */

import * as React from "react";
import cytoscape from "cytoscape";

interface OntologyClass {
  id: string;
  label: string;
  comment?: string;
  subClassOf?: string;
  equivalentClass?: string;
}

interface OntologyProperty {
  id: string;
  label: string;
  comment?: string;
  domain?: string;
  range?: string;
  type: 'ObjectProperty' | 'DatatypeProperty';
}

interface OntologyIndividual {
  id: string;
  label: string;
  type: string;
  sameAs?: string;
}

interface OntologyAlignment {
  source: string;
  relation: 'equivalentClass' | 'subClassOf' | 'subPropertyOf' | 'closeMatch' | 'exactMatch';
  target: string;
  vocabulary: 'DCAT' | 'PROV' | 'Schema.org' | 'SKOS' | 'OBI' | 'EFO';
}

interface OntologyV3Structure {
  classes: OntologyClass[];
  objectProperties: OntologyProperty[];
  dataProperties: OntologyProperty[];
  individuals: OntologyIndividual[];
  alignments: OntologyAlignment[];
  meta: {
    namespace: string;
    title: string;
    created: string;
    creator: string;
    parsedAt: string;
  };
}

type ViewTab = 'graph' | 'classes' | 'properties' | 'individuals' | 'alignments';

export function OntologyStructureViewer(): React.ReactElement {
  const [ontology, setOntology] = React.useState<OntologyV3Structure | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [activeTab, setActiveTab] = React.useState<ViewTab>('graph');
  const [expandedClasses, setExpandedClasses] = React.useState<Set<string>>(new Set());
  const [expandedIndividuals, setExpandedIndividuals] = React.useState<Set<string>>(new Set());
  const graphContainerRef = React.useRef<HTMLDivElement>(null);
  const cyRef = React.useRef<cytoscape.Core | null>(null);

  // Fetch ontology structure on mount
  React.useEffect(() => {
    const fetchOntology = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/ontology/v3/structure');

        if (!response.ok) {
          throw new Error(`Failed to load ontology: ${response.status} ${response.statusText}`);
        }

        const data: OntologyV3Structure = await response.json();
        setOntology(data);
      } catch (err) {
        console.error("[OntologyStructureViewer] Failed to fetch ontology:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    fetchOntology();
  }, []);

  // Initialize Cytoscape graph when ontology loads and graph tab is active
  React.useEffect(() => {
    if (!ontology || !graphContainerRef.current || activeTab !== 'graph') {
      return;
    }

    // Track if component is still mounted
    let isMounted = true;

    // Destroy existing instance
    if (cyRef.current) {
      cyRef.current.removeAllListeners();
      cyRef.current.destroy();
    }

    // Build graph elements
    const elements: cytoscape.ElementDefinition[] = [];

    // Add class nodes
    ontology.classes.forEach((cls) => {
      elements.push({
        data: {
          id: cls.id,
          label: cls.label,
          type: 'class',
          comment: cls.comment,
        },
        classes: 'class-node',
      });
    });

    // Build set of existing class IDs for validation
    const classIds = new Set(ontology.classes.map(c => c.id));

    // Add subClassOf edges (only if target exists)
    ontology.classes.forEach((cls) => {
      if (cls.subClassOf && classIds.has(cls.subClassOf)) {
        elements.push({
          data: {
            id: `subClassOf_${cls.id}`,
            source: cls.id,
            target: cls.subClassOf,
            label: 'subClassOf',
            type: 'hierarchy',
          },
          classes: 'hierarchy-edge',
        });
      }
    });

    // Add property edges (only if both domain and range exist)
    ontology.objectProperties.forEach((prop) => {
      if (prop.domain && prop.range && classIds.has(prop.domain) && classIds.has(prop.range)) {
        elements.push({
          data: {
            id: prop.id,
            source: prop.domain,
            target: prop.range,
            label: prop.label,
            type: 'property',
          },
          classes: 'property-edge',
        });
      }
    });

    // Initialize Cytoscape
    const cy = cytoscape({
      container: graphContainerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#56B4E9',  // Colorblind-friendly sky blue (Okabe-Ito)
            'label': 'data(label)',
            'color': '#fff',
            'text-valign': 'center',
            'text-halign': 'center',
            'font-size': '12px',
            'font-weight': 'bold',
            'width': 'label',  // Auto-size based on label
            'height': 'label',
            'padding': '18px',
            'text-wrap': 'wrap',
            'text-max-width': '140px',
            'border-width': '3px',
            'border-color': '#0072B2',  // Darker blue
            'shape': 'roundrectangle',
          },
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 5,
            'border-color': '#E69F00',  // Colorblind-friendly orange
            'overlay-color': '#E69F00',
            'overlay-opacity': 0.2,
          },
        },
        {
          selector: 'edge',
          style: {
            'width': 2,
            'line-color': '#95a5a6',
            'target-arrow-color': '#95a5a6',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'label': 'data(label)',
            'font-size': '11px',
            'color': '#2c3e50',
            'text-background-color': '#ffffff',
            'text-background-opacity': 0.85,
            'text-background-padding': '3px',
            'text-rotation': 'autorotate',
            'opacity': 0.7,
          },
        },
        {
          selector: 'edge:selected',
          style: {
            'opacity': 1.0,
            'width': 5,
          },
        },
        {
          selector: '.hierarchy-edge',
          style: {
            'width': 4,
            'line-color': '#0072B2',  // Strong blue
            'target-arrow-color': '#0072B2',
            'line-style': 'solid',
            'opacity': 0.9,
          },
        },
        {
          selector: '.property-edge',
          style: {
            'width': 2,
            'line-color': '#D55E00',  // Vermillion for semantic relationships
            'target-arrow-color': '#D55E00',
            'line-style': 'dashed',
            'line-dash-pattern': [6, 3],
            'opacity': 0.6,
          },
        },
      ],
      layout: {
        name: 'breadthfirst',
        directed: true,
        spacingFactor: 2.0,  // Increased from 1.5 for better spacing
        padding: 40,
        animate: true,
        animationDuration: 500,
      },
      minZoom: 0.3,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    });

    cyRef.current = cy;

    // Cleanup
    return () => {
      isMounted = false;
      // Prevent state updates after unmount using isMounted flag
      void isMounted;

      if (cyRef.current && !cyRef.current.destroyed()) {
        cyRef.current.removeAllListeners();
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [ontology, activeTab]);

  // Toggle class expansion
  const toggleClass = (classId: string) => {
    setExpandedClasses((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
      } else {
        next.add(classId);
      }
      return next;
    });
  };

  // Toggle individual group expansion
  const toggleIndividualGroup = (groupType: string) => {
    setExpandedIndividuals((prev) => {
      const next = new Set(prev);
      if (next.has(groupType)) {
        next.delete(groupType);
      } else {
        next.add(groupType);
      }
      return next;
    });
  };

  // Extract short name from IRI
  const getShortName = (iri: string): string => {
    const parts = iri.split(/[/#]/);
    return parts[parts.length - 1] || iri;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="ontology-structure-viewer loading">
        <div className="loading-spinner"></div>
        <p>Loading FAIR Ontology structure...</p>
      </div>
    );
  }

  // Render error state
  if (error || !ontology) {
    return (
      <div className="ontology-structure-viewer error">
        <div className="error-icon">⚠️</div>
        <h2>Unable to Load Ontology Structure</h2>
        <p>{error?.message ?? "Unknown error"}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Build class hierarchy (root classes without valid parent)
  const classIds = new Set(ontology.classes.map(c => c.id));
  const rootClasses = ontology.classes.filter(
    (cls) => !cls.subClassOf || !classIds.has(cls.subClassOf)
  );
  const getSubClasses = (parentId: string) =>
    ontology.classes.filter((cls) => cls.subClassOf === parentId);

  // Group individuals by type
  const individualsByType = ontology.individuals.reduce((acc, ind) => {
    if (!acc[ind.type]) {
      acc[ind.type] = [];
    }
    acc[ind.type].push(ind);
    return acc;
  }, {} as Record<string, OntologyIndividual[]>);

  // Recursive class tree component
  const ClassTreeNode = ({ cls, level = 0 }: { cls: OntologyClass; level?: number }) => {
    const subClasses = getSubClasses(cls.id);
    const hasChildren = subClasses.length > 0;
    const isExpanded = expandedClasses.has(cls.id);

    return (
      <div className="class-tree-node" style={{ marginLeft: `${level * 20}px` }}>
        <div className="class-header">
          {hasChildren && (
            <button
              className="expand-btn"
              onClick={() => toggleClass(cls.id)}
              data-action="toggle-class"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span className="expand-spacer">•</span>}
          <div className="class-info">
            <span className="class-label">{cls.label}</span>
            <span className="class-id">{getShortName(cls.id)}</span>
            {cls.equivalentClass && (
              <span className="class-equivalent" title={`Equivalent to ${cls.equivalentClass}`}>
                ≡ {getShortName(cls.equivalentClass)}
              </span>
            )}
          </div>
        </div>
        {cls.comment && (
          <div className="class-comment">{cls.comment}</div>
        )}
        {isExpanded && hasChildren && (
          <div className="class-children">
            {subClasses.map((subCls) => (
              <ClassTreeNode key={subCls.id} cls={subCls} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ontology-structure-viewer">
      {/* Header */}
      <header className="ontology-structure-header">
        <h1>{ontology.meta.title}</h1>
        <div className="ontology-meta">
          <span>Created: {ontology.meta.created}</span>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="ontology-tabs">
        <button
          className={activeTab === 'graph' ? 'active' : ''}
          onClick={() => setActiveTab('graph')}
          data-action="tab-graph"
        >
          Graph View
        </button>
        <button
          className={activeTab === 'classes' ? 'active' : ''}
          onClick={() => setActiveTab('classes')}
          data-action="tab-classes"
        >
          Classes ({ontology.classes.length})
        </button>
        <button
          className={activeTab === 'properties' ? 'active' : ''}
          onClick={() => setActiveTab('properties')}
          data-action="tab-properties"
        >
          Properties ({ontology.objectProperties.length + ontology.dataProperties.length})
        </button>
        <button
          className={activeTab === 'individuals' ? 'active' : ''}
          onClick={() => setActiveTab('individuals')}
          data-action="tab-individuals"
        >
          Individuals ({ontology.individuals.length})
        </button>
        <button
          className={activeTab === 'alignments' ? 'active' : ''}
          onClick={() => setActiveTab('alignments')}
          data-action="tab-alignments"
        >
          Alignments ({ontology.alignments.length})
        </button>
      </nav>

      {/* Content Area */}
      <div className="ontology-content">
        {/* Graph Tab */}
        {activeTab === 'graph' && (
          <section className="graph-panel">
            <div className="graph-controls">
              <h2>Interactive Ontology Graph</h2>
              <p className="panel-description">
                This graph visualizes the DATA Compass ontology as a network of interconnected concepts using a
                colorblind-friendly palette (Okabe-Ito). Sky blue rectangles represent classes (like Dataset, API, License),
                solid blue arrows show inheritance relationships (subClassOf), and dashed vermillion arrows represent
                properties connecting classes (like "hasIdentifier" linking Dataset to PersistentIdentifier).
                Click nodes to select them (highlighted with orange glow). Use your mouse wheel to zoom in/out and drag to pan around the graph.
              </p>
            </div>
            <div
              ref={graphContainerRef}
              className="ontology-graph-container"
              style={{ width: '100%', height: '600px', border: '1px solid #1c2a54', borderRadius: '8px' }}
            />
          </section>
        )}

        {/* Classes Tab */}
        {activeTab === 'classes' && (
          <section className="classes-panel">
            <h2>Class Hierarchy</h2>
            <p className="panel-description">
              Classes define the main concepts in the FAIR ontology (e.g., Dataset, License, API, PersistentIdentifier).
              The tree structure shows inheritance relationships where specialized classes (like RESTfulAPI) inherit from
              more general classes (like API). Click the arrows to expand and explore subclasses. Purple badges indicate
              equivalent classes that map to standard vocabularies like DCAT and PROV.
            </p>
            <div className="class-tree">
              {rootClasses.map((cls) => (
                <ClassTreeNode key={cls.id} cls={cls} />
              ))}
            </div>
          </section>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <section className="properties-panel">
            <h2>Object Properties ({ontology.objectProperties.length})</h2>
            <p className="panel-description">
              Object properties define typed relationships between classes in the ontology. For example, "hasIdentifier"
              connects a Dataset to a PersistentIdentifier, while "accessedVia" links a Dataset to an API. Each property
              shows its domain (source class) and range (target class) in the signature line below its description.
              These properties enable semantic querying and reasoning about FAIR compliance relationships.
            </p>
            <div className="property-list">
              {ontology.objectProperties.map((prop) => (
                <div key={prop.id} className="property-card">
                  <div className="property-header">
                    <span className="property-label">{prop.label}</span>
                    <span className="property-type">Object Property</span>
                  </div>
                  {prop.comment && <p className="property-comment">{prop.comment}</p>}
                  <div className="property-signature">
                    <span className="property-domain" title="Domain">
                      {prop.domain ? getShortName(prop.domain) : '?'}
                    </span>
                    <span className="property-arrow">→</span>
                    <span className="property-range" title="Range">
                      {prop.range ? getShortName(prop.range) : '?'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <h2 style={{ marginTop: '2rem' }}>Data Properties ({ontology.dataProperties.length})</h2>
            <p className="panel-description">
              Data properties link classes to literal values like strings, numbers, booleans, or dates. For example,
              "uptimePercent" stores a decimal value on ServiceLevelAgreement, and "identifierValue" stores the actual
              PID string on a PersistentIdentifier. These properties capture concrete data values rather than relationships.
            </p>
            <div className="property-list">
              {ontology.dataProperties.map((prop) => (
                <div key={prop.id} className="property-card">
                  <div className="property-header">
                    <span className="property-label">{prop.label}</span>
                    <span className="property-type data-property">Data Property</span>
                  </div>
                  {prop.comment && <p className="property-comment">{prop.comment}</p>}
                  <div className="property-signature">
                    <span className="property-domain" title="Domain">
                      {prop.domain ? getShortName(prop.domain) : '?'}
                    </span>
                    <span className="property-arrow">→</span>
                    <span className="property-range" title="Range">
                      {prop.range ? getShortName(prop.range) : 'Literal'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Individuals Tab */}
        {activeTab === 'individuals' && (
          <section className="individuals-panel">
            <h2>Named Individuals</h2>
            <p className="panel-description">
              Named individuals are specific, enumerated instances of classes defined in the ontology. For example,
              DOI, Handle, and ARK are individual PIDSchemes, while CC BY 4.0 and MIT are individual Licenses.
              These represent concrete, reusable values that can be referenced in FAIR assessments. Click group headers
              to expand and see all individuals of each type. External links show equivalent concepts in standard registries.
            </p>
            {Object.entries(individualsByType).map(([type, individuals]) => {
              const isExpanded = expandedIndividuals.has(type);
              return (
                <div key={type} className="individual-group">
                  <button
                    className="group-header"
                    onClick={() => toggleIndividualGroup(type)}
                    data-action="toggle-individual-group"
                  >
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    <span className="group-title">{type} ({individuals.length})</span>
                  </button>
                  {isExpanded && (
                    <div className="individual-list">
                      {individuals.map((ind) => (
                        <div key={ind.id} className="individual-card">
                          <div className="individual-label">{ind.label}</div>
                          <div className="individual-id">{getShortName(ind.id)}</div>
                          {ind.sameAs && (
                            <div className="individual-same-as">
                              <span className="same-as-label">Same as:</span>
                              <a href={ind.sameAs} target="_blank" rel="noopener noreferrer">
                                {getShortName(ind.sameAs)}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* Alignments Tab */}
        {activeTab === 'alignments' && (
          <section className="alignments-panel">
            <h2>Standard Vocabulary Alignments</h2>
            <p className="panel-description">
              Alignments show how DATA Compass ontology concepts map to widely-adopted W3C and community standards.
              For example, our "Dataset" class is equivalent (≡) to DCAT's Dataset, and our "wasGeneratedBy" property
              closely matches (≈) PROV's wasGeneratedBy. These mappings enable interoperability with existing Linked Data
              systems, RDF stores, and semantic web applications. Symbols indicate relationship types: ≡ (equivalent),
              ⊆ (subclass), ⊑ (subproperty), ≈ (close match), = (exact match).
            </p>

            {/* Group by vocabulary */}
            {['DCAT', 'PROV', 'Schema.org', 'SKOS', 'OBI', 'EFO'].map((vocab) => {
              const vocabAlignments = ontology.alignments.filter((a) => a.vocabulary === vocab);
              if (vocabAlignments.length === 0) return null;

              return (
                <div key={vocab} className="alignment-group">
                  <h3>{vocab} ({vocabAlignments.length})</h3>
                  <div className="alignment-list">
                    {vocabAlignments.map((alignment, idx) => (
                      <div key={idx} className="alignment-card">
                        <div className="alignment-source">{getShortName(alignment.source)}</div>
                        <div className="alignment-relation">
                          {alignment.relation === 'equivalentClass' && '≡'}
                          {alignment.relation === 'subClassOf' && '⊆'}
                          {alignment.relation === 'subPropertyOf' && '⊑'}
                          {alignment.relation === 'closeMatch' && '≈'}
                          {alignment.relation === 'exactMatch' && '='}
                        </div>
                        <div className="alignment-target">
                          <a href={alignment.target} target="_blank" rel="noopener noreferrer">
                            {getShortName(alignment.target)}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
}
